import { DEFAULT_STATION, type Category, type OrderLine, type Product } from '../types'

export interface StationSlip {
  station: string
  lines: OrderLine[]
  totalKurus: number
  itemCount: number
}

/**
 * Splits an order into one slip per counter, because a customer carrying a
 * single slip can only hand it to one stall, the grill never sees what the
 * drinks counter owes them.
 *
 * Order follows first appearance in the order rather than anything
 * alphabetical, so the slips come off the printer in the sequence the items
 * were rung up and stay easy to match against the screen.
 */
export function splitByStation(lines: OrderLine[]): StationSlip[] {
  const slips = new Map<string, StationSlip>()

  for (const line of lines) {
    const station = line.station || DEFAULT_STATION
    const slip = slips.get(station)
    if (slip) {
      slip.lines.push(line)
      slip.totalKurus += line.lineTotalKurus
      slip.itemCount += line.qty
    } else {
      slips.set(station, {
        station,
        lines: [line],
        totalKurus: line.lineTotalKurus,
        itemCount: line.qty,
      })
    }
  }

  return [...slips.values()]
}

/**
 * Basılacak tezgah fişleri: "fiş basılmasın" denen tezgahlar (yalnız mutfak
 * ekranından takip edenler) çıkıyor. Hepsi çıkarsa boş liste, o zaman hiçbir
 * yazıcıya bir şey gitmiyor.
 */
export function printedSlips(slips: StationSlip[], settings?: { skipStations?: string[] }): StationSlip[] {
  const skip = settings?.skipStations
  return skip && skip.length > 0 ? slips.filter((s) => !skip.includes(s.station)) : slips
}

/**
 * İşletmedeki bütün tezgah adları, alfabe sırasıyla (21 Eki 2026).
 *
 * Kategorilerin tezgahları ve ürüne özel verilen tezgahlar birlikte: bir
 * ürüne yeni bir tezgah ("Nargile") yazılınca o tezgah da yazıcı ve mutfak
 * ekranı ayarlarında seçilebilsin. Kasada gizli ürünün tezgahı sayılmıyor.
 */
export function stationNames(
  categories: Pick<Category, 'station'>[],
  products: Pick<Product, 'station' | 'active'>[] = [],
): string[] {
  const set = new Set<string>()
  for (const c of categories) set.add(c.station || DEFAULT_STATION)
  for (const p of products) if (p.station && p.active !== false) set.add(p.station)
  return [...set].sort((a, b) => a.localeCompare(b, 'tr'))
}
